import { getLanguage, l, t } from "../core/i18n.js";
import { RECOVERY_ROUTES, renderRecovery } from "./account-recovery.js";
import { api, flush, getSyncInfo } from "../services/storage.js";
import { runtime } from "../core/runtime.js";
import { dayxMark } from "./landing.js";
import { languageSelect } from "../core/i18n.js";
import { icon } from "./icons.js";
import { fullscreenButton } from "./fullscreen.js";

export const googleMark =
  '<img src="/google-g.png" width="20" height="20" alt="" aria-hidden="true">';
const mailMark =
  '<svg class="ui-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>';
const messages = {
  google_unavailable:
    "Google sign-in is not available yet. Please use email below.",
  google_expired: "Your Google sign-in expired. Please try again.",
  google_cancelled:
    "Google sign-in was cancelled. You can try again or use email.",
  google_failed: "We couldn't complete Google sign-in. Please try again.",
  link_required:
    "This email already has an account. Log in with your password, then connect Google from your account settings.",
  account_mismatch:
    "Choose the Google account with the same email as your workspace.",
  already_signed_in: "You're already signed in. Continue to your workspace.",
};

// Guest changes must reach the server before the session switches accounts.
async function saveGuestWork() {
  if (!getSyncInfo().requiresLogin && !(await flush()))
    throw new Error(
      l("Your changes are still saving. Please try again in a moment."),
    );
}
function enterWorkspace() {
  runtime.skipUnloadSave = true;
  location.assign("/today");
}

export async function startGoogle(mode) {
  await saveGuestWork();
  const { url } = await api("/api/auth/google/start", {
    method: "POST",
    data: { mode },
  });
  runtime.skipUnloadSave = true;
  location.assign(url);
}

// One Tap lists the visitor's Google accounts right on the page. It needs this
// site in the client's Authorized JavaScript origins; without that, Google
// simply shows nothing and the button below still works.
let gisLoading = null;
function loadGoogleIdentity() {
  gisLoading ||= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://accounts.google.com/gsi/client?hl=${getLanguage()}`;
    script.async = true;
    script.onload = () => resolve(window.google?.accounts?.id);
    script.onerror = () => {
      gisLoading = null;
      reject(new Error("Google Identity Services did not load."));
    };
    document.head.append(script);
  });
  return gisLoading;
}
async function offerGoogleAccounts(clientId, signup, onError) {
  try {
    const [identity, { nonce }] = await Promise.all([
      loadGoogleIdentity(),
      api("/api/auth/google/nonce", { method: "POST", data: {} }),
    ]);
    if (!identity || !document.getElementById("googleAuth")) return;
    identity.initialize({
      client_id: clientId,
      nonce,
      context: signup ? "signup" : "signin",
      ux_mode: "popup",
      itp_support: true,
      use_fedcm_for_prompt: true,
      cancel_on_tap_outside: false,
      callback: async ({ credential }) => {
        try {
          await saveGuestWork();
          await api("/api/auth/google/onetap", {
            method: "POST",
            data: { credential, nonce },
          });
          enterWorkspace();
        } catch (problem) {
          onError(problem.message);
        }
      },
    });
    identity.prompt();
  } catch {
    // The regular Google button remains available.
  }
}

function sheetHTML(signup) {
  const passwordLabel = signup
    ? `<span id="passwordHint">${t("8 characters minimum")}</span>`
    : "";
  return `<div class="auth-sheet" id="authSheet" hidden>
    <div class="auth-sheet-backdrop" data-sheet-close></div>
    <section class="auth-sheet-panel" role="dialog" aria-modal="true" aria-labelledby="authSheetTitle">
      <span class="auth-sheet-grip" aria-hidden="true"></span>
      <header class="auth-sheet-head"><h2 id="authSheetTitle">${t("Continue with email")}</h2><button type="button" class="auth-sheet-close" data-sheet-close aria-label="${t("Close")}">${icon("close")}</button></header>
      <form id="codeForm" class="auth-step" novalidate hidden>
        <label for="codeEmail">${t("Email address")}</label>
        <input class="field" id="codeEmail" type="email" autocomplete="email" inputmode="email" placeholder="you@example.com" required maxlength="254">
        <p class="auth-step-note">${t("We'll email you a 6-digit code. No password needed.")}</p>
        <p class="auth-error" role="alert"></p>
        <button class="btn black auth-submit" type="submit">${t("Send code")} ${icon("arrow")}</button>
      </form>
      <form id="codeVerify" class="auth-step" novalidate hidden>
        <p class="auth-step-note" id="codeSentTo"></p>
        <label for="codeInput">${t("6-digit code")}</label>
        <input class="field auth-code-input" id="codeInput" type="text" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]*" maxlength="6" placeholder="••••••" required>
        <p class="auth-error" role="alert"></p>
        <button class="btn black auth-submit" type="submit">${t("Continue")} ${icon("arrow")}</button>
        <div class="auth-step-links"><button type="button" data-code-resend>${t("Send a new code")}</button><button type="button" data-code-change>${t("Change email")}</button></div>
      </form>
      <form id="authForm" class="auth-step" hidden>
        <label for="authEmail">${t("Email address")}</label>
        <input class="field" id="authEmail" name="email" type="email" autocomplete="email" placeholder="you@example.com" required maxlength="254">
        <div class="auth-password-label"><label for="authPassword">${t("Password")}</label>${passwordLabel}</div>
        <div class="auth-password-field"><input class="field" id="authPassword" name="password" type="password" autocomplete="${signup ? "new-password" : "current-password"}" placeholder="${signup ? t("Create a strong password") : t("Enter your password")}" required minlength="8" maxlength="256" ${signup ? 'aria-describedby="passwordHint"' : ""}><button type="button" id="togglePassword" aria-label="${t("Show password")}" aria-pressed="false">${t("Show")}</button></div>
        <p class="auth-error" role="alert"></p>
        <button class="btn black auth-submit" type="submit">${signup ? t("Create account") : t("Log in")} ${icon("arrow")}</button>
        ${signup ? "" : `<a class="auth-forgot" href="/forgot-password" hidden>${t("Forgot your password?")}</a>`}
      </form>
      <button type="button" class="auth-sheet-switch" id="sheetSwitch" hidden></button>
    </section>
  </div>`;
}

export function renderAuthPage() {
  const signup = location.pathname === "/signup";
  const mode = signup ? "register" : "login";
  const host = document.getElementById("authPage");
  const legal = signup
    ? `<p class="auth-legal">${t("By creating an account, you agree to the {terms} and {privacy}.", { terms: `<a href="/${getLanguage()}/terms">${t("the Terms of use")}</a>`, privacy: `<a href="/${getLanguage()}/privacy">${t("the Privacy policy")}</a>` })}</p>`
    : "";
  host.innerHTML = `${l('<aside class="auth-story"><a class="wordmark" href="/" aria-label="DayX home">')}${dayxMark}${l('</a><div class="auth-story-copy"><span class="auth-eyebrow">A SPACE FOR YOUR AMBITION</span><h2>Good days start<br>with <em>intention.</em></h2><p>Your plans. Your focus. Your next small win.<br>All together, in a space that feels like you.</p><div class="auth-plan-art" aria-hidden="true"><div class="auth-plan-label">YOUR NEXT CHAPTER ')}${icon("spark")}${l("</div><strong>One priority.<br>A little progress.</strong><div><span>")}${icon("check")}${l("</span> Make room for what matters</div><div><span>")}${icon("check")}${l('</span> Build a rhythm of your own</div><div><span class="unfinished"></span> Take the next small step</div></div></div><span class="auth-story-foot">YOUR PACE. YOUR POSSIBILITIES.</span></aside>')}<main class="auth-main"><nav class="auth-nav" aria-label="${t("Account navigation")}"><a class="wordmark auth-home" href="/" aria-label="${t("DayX home")}">${dayxMark}</a><div class="auth-nav-tools">${fullscreenButton()}${languageSelect()}</div></nav>
    <div class="auth-form-wrap">
      <div class="auth-eyebrow">${signup ? l("YOUR FRESH START") : l("YOUR DAILY WORKSPACE")}</div>
      <h1>${signup ? l("Create your account.") : l("Welcome back.")}</h1>
      <p class="auth-intro">${signup ? l("A little clarity for your day. A place for your goals.") : l("Your plans and progress, right where you left them.")}</p>
      <p class="auth-error" id="authError" role="alert"></p>
      <div class="auth-providers">
        <button class="google-button" id="googleAuth" type="button" disabled>${googleMark}<span>${t("Continue with Google")}</span></button>
        <p class="auth-provider-note" id="googleAvailability" role="status"></p>
        <button class="auth-email-button" id="emailAuth" type="button">${mailMark}<span>${t("Continue with email")}</span></button>
      </div>
      ${legal}
      <p class="auth-switch">${signup ? t("Already have an account?") : t("New to DayX?")} <a href="${signup ? "/login" : "/signup"}">${signup ? t("Log in") : t("Sign up")}</a></p>
      <a class="auth-guest" href="/today">${t("Continue as a guest")} <span aria-hidden="true">↗</span></a>
    </div>
    ${sheetHTML(signup)}</main>`;
  if (RECOVERY_ROUTES.includes(location.pathname)) {
    host.querySelector("#authSheet").remove();
    renderRecovery(host);
    return;
  }
  const error = host.querySelector("#authError");
  const params = new URLSearchParams(location.search);
  error.textContent = l(messages[params.get("error")] || "");
  const sync = getSyncInfo();
  const signedIn = !!sync.session?.user && !sync.requiresLogin;
  if (sync.requiresLogin) {
    const email = sync.session?.user?.email || "";
    host.querySelector("#authEmail").value = email;
    host.querySelector("#codeEmail").value = email;
  }
  if (signedIn) {
    host.querySelector(".auth-providers").hidden = true;
    host.querySelector(".auth-switch").hidden = true;
    host.querySelector(".auth-legal")?.remove();
    const guest = host.querySelector(".auth-guest");
    guest.textContent = l("Continue to your workspace →");
    guest.classList.add("auth-signed-in");
    if (!error.textContent) error.textContent = l("You're already signed in.");
    return;
  }

  async function action(button, task, label, errorBox = error) {
    errorBox.textContent = "";
    button.disabled = true;
    const original = button.innerHTML;
    button.textContent = label;
    try {
      await task();
    } catch (problem) {
      errorBox.textContent = problem.message;
      button.disabled = false;
      button.innerHTML = original;
    }
  }

  // Email sheet: a code by email when delivery is configured, otherwise a password.
  const sheet = host.querySelector("#authSheet");
  const steps = {
    code: host.querySelector("#codeForm"),
    verify: host.querySelector("#codeVerify"),
    password: host.querySelector("#authForm"),
  };
  const switcher = host.querySelector("#sheetSwitch");
  let codesAvailable = false;
  let codeEmail = "";
  let lastFocus = null;
  function show(step) {
    for (const [name, form] of Object.entries(steps))
      form.hidden = name !== step;
    switcher.hidden = !codesAvailable || step === "verify";
    switcher.textContent =
      step === "password"
        ? t("Get a code by email instead")
        : t("Use a password instead");
    switcher.dataset.target = step === "password" ? "code" : "password";
    steps[step].querySelector(".auth-error").textContent = "";
    requestAnimationFrame(() =>
      steps[step].querySelector("input")?.focus({ preventScroll: true }),
    );
  }
  function openSheet() {
    lastFocus = document.activeElement;
    sheet.hidden = false;
    document.body.classList.add("auth-sheet-open");
    requestAnimationFrame(() => sheet.classList.add("is-open"));
    show(codesAvailable ? "code" : "password");
  }
  function closeSheet() {
    sheet.classList.remove("is-open");
    document.body.classList.remove("auth-sheet-open");
    setTimeout(() => {
      if (!sheet.classList.contains("is-open")) sheet.hidden = true;
    }, 260);
    lastFocus?.focus({ preventScroll: true });
  }
  host.querySelector("#emailAuth").onclick = openSheet;
  sheet
    .querySelectorAll("[data-sheet-close]")
    .forEach((el) => (el.onclick = closeSheet));
  sheet.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeSheet();
  });
  switcher.onclick = () => show(switcher.dataset.target);

  async function sendCode() {
    await api("/api/auth/code/send", {
      method: "POST",
      data: { email: codeEmail, language: getLanguage() },
    });
    host.querySelector("#codeSentTo").textContent = t(
      "We sent a 6-digit code to {email}.",
      { email: codeEmail },
    );
    host.querySelector("#codeInput").value = "";
    show("verify");
  }
  steps.code.onsubmit = (event) => {
    event.preventDefault();
    const input = host.querySelector("#codeEmail");
    const errorBox = steps.code.querySelector(".auth-error");
    if (!input.checkValidity()) {
      errorBox.textContent = t("Enter a valid email address.");
      return;
    }
    codeEmail = input.value.trim();
    const button = steps.code.querySelector("[type=submit]");
    action(button, () => sendCode(), t("Sending…"), errorBox);
  };
  steps.verify.onsubmit = (event) => {
    event.preventDefault();
    const errorBox = steps.verify.querySelector(".auth-error");
    const code = host.querySelector("#codeInput").value.replace(/\D/g, "");
    if (code.length !== 6) {
      errorBox.textContent = t("Enter the 6-digit code from the email.");
      return;
    }
    action(
      steps.verify.querySelector("[type=submit]"),
      async () => {
        await saveGuestWork();
        await api("/api/auth/code/verify", {
          method: "POST",
          data: { email: codeEmail, code },
        });
        enterWorkspace();
      },
      t("Checking…"),
      errorBox,
    );
  };
  // Pasting or autofilling the full code continues straight away.
  host.querySelector("#codeInput").addEventListener("input", (event) => {
    event.target.value = event.target.value.replace(/\D/g, "").slice(0, 6);
    if (event.target.value.length === 6) steps.verify.requestSubmit();
  });
  steps.verify.querySelector("[data-code-resend]").onclick = (event) => {
    const errorBox = steps.verify.querySelector(".auth-error");
    action(event.currentTarget, () => sendCode(), t("Sending…"), errorBox);
  };
  steps.verify.querySelector("[data-code-change]").onclick = () => show("code");

  steps.password.onsubmit = (event) => {
    event.preventDefault();
    const errorBox = steps.password.querySelector(".auth-error");
    action(
      event.currentTarget.querySelector("[type=submit]"),
      async () => {
        await saveGuestWork();
        await api("/api/auth/" + mode, {
          method: "POST",
          data: {
            email: host.querySelector("#authEmail").value,
            password: host.querySelector("#authPassword").value,
            language: getLanguage(),
          },
        });
        enterWorkspace();
      },
      signup ? l("Creating your account…") : l("Logging in…"),
      errorBox,
    );
  };
  host.querySelector("#togglePassword").onclick = (event) => {
    const input = host.querySelector("#authPassword");
    const visible = input.type === "password";
    input.type = visible ? "text" : "password";
    event.currentTarget.textContent = visible ? t("Hide") : t("Show");
    event.currentTarget.setAttribute(
      "aria-label",
      visible ? t("Hide password") : t("Show password"),
    );
    event.currentTarget.setAttribute("aria-pressed", String(visible));
  };

  const google = host.querySelector("#googleAuth");
  const availability = host.querySelector("#googleAvailability");
  api("/api/auth/config")
    .then((config) => {
      google.disabled = !config.google;
      codesAvailable = !!config.email;
      const forgot = host.querySelector(".auth-forgot");
      if (forgot) forgot.hidden = !config.email;
      availability.textContent = config.google
        ? ""
        : l("Google sign-in is coming soon. You can use email today.");
      availability.hidden = !!config.google;
      if (config.google && config.googleClientId)
        offerGoogleAccounts(config.googleClientId, signup, (message) => {
          error.textContent = message;
        });
    })
    .catch(() => {
      availability.textContent = l(
        "Google sign-in is temporarily unavailable.",
      );
    });
  google.onclick = () =>
    action(
      google,
      () => startGoogle(signup ? "signup" : "login"),
      l("Connecting to Google…"),
    );
}
